// Highlight layers over a rendered PDF page: a multiply-blended layer (so the page's ink stays black) with the clause paragraph tint and green bar, the source-text marks with their underline and the value marks, and a normal layer above it with the label of the active or hovered value, the green outline and tag of a figure the curve was digitised from, and the scan sweep; PDF points are scaled to CSS pixels and positioned through the CSSOM.
import { ChartLineIcon } from "lucide-react";
import { useState, type CSSProperties } from "react";

import type { Rect } from "@/api/__generated__";

import { figureTag, paragraphBlocks, rectKey, type ViewerMarks } from "./highlights";

export interface HighlightLayerProps {
  marks: ViewerMarks | null;
  scale: number;
  visible: boolean;
  activeValueKey?: string | null;
  flashValues?: boolean;
  scanKey?: string | null;
}

const PARA_PAD = { x: 3, y: 1.5 };
const TEXT_PAD = { x: 1, y: 0.5 };
const VALUE_PAD = { x: 1.5, y: 1 };
const BAR = { gap: 9, width: 3 };
const LABEL_GAP_PX = 5;

export function boxStyle(
  rect: Rect,
  scale: number,
  pad: { x: number; y: number } = { x: 0, y: 0 },
): CSSProperties {
  return {
    left: (rect.x0 - pad.x) * scale,
    top: (rect.y0 - pad.y) * scale,
    width: (rect.x1 - rect.x0 + 2 * pad.x) * scale,
    height: (rect.y1 - rect.y0 + 2 * pad.y) * scale,
  };
}

function barStyle(block: Rect, scale: number): CSSProperties {
  return {
    left: (block.x0 - BAR.gap) * scale,
    top: (block.y0 - PARA_PAD.y) * scale,
    width: Math.max(3, BAR.width * scale),
    height: (block.y1 - block.y0 + 2 * PARA_PAD.y) * scale,
  };
}

function labelStyle(rect: Rect, scale: number): CSSProperties {
  return {
    left: ((rect.x0 + rect.x1) / 2) * scale,
    top: (rect.y0 - VALUE_PAD.y) * scale - LABEL_GAP_PX,
  };
}

export function HighlightLayer({
  marks,
  scale,
  visible,
  activeValueKey = null,
  flashValues = false,
  scanKey = null,
}: HighlightLayerProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  if (!visible || marks === null) {
    return null;
  }
  const blocks = paragraphBlocks(marks.clause);
  const lit = (key: string) => flashValues || key === activeValueKey || key === hovered;
  const layerStyle = { "--mark-scale": scale } as CSSProperties;
  return (
    <>
      <div className="pdf-marks" aria-hidden data-testid="pdf-marks" style={layerStyle}>
        {blocks.map((block) => (
          <div key={rectKey(block)}>
            <div
              className="pdf-mark pdf-mark--para"
              data-mark="paragraph"
              style={boxStyle(block, scale, PARA_PAD)}
            />
            <div className="pdf-mark pdf-mark--bar" data-mark="bar" style={barStyle(block, scale)} />
          </div>
        ))}
        {marks.text.map((rect) => (
          <div
            key={rectKey(rect)}
            className="pdf-mark pdf-mark--text"
            data-mark="text"
            style={boxStyle(rect, scale, TEXT_PAD)}
          />
        ))}
        {marks.values.map((value) =>
          value.rects.map((rect) => (
            <div
              key={`${value.key}:${rectKey(rect)}`}
              className="pdf-mark pdf-mark--value"
              data-mark="value"
              data-key={value.key}
              data-active={lit(value.key) ? "true" : "false"}
              style={boxStyle(rect, scale, VALUE_PAD)}
              onPointerEnter={() => {
                setHovered(value.key);
              }}
              onPointerLeave={() => {
                setHovered((current) => (current === value.key ? null : current));
              }}
            />
          )),
        )}
      </div>
      <div className="pdf-overlay" aria-hidden style={layerStyle}>
        {scanKey === null ? null : <div key={scanKey} className="pdf-scan animate-scan" />}
        {marks.values
          .filter((value) => lit(value.key) && value.rects.length > 0)
          .map((value) => {
            const first = value.rects[0];
            return first === undefined ? null : (
              <span
                key={value.key}
                className="pdf-mark__label"
                data-key={value.key}
                style={labelStyle(first, scale)}
              >
                {value.label}
              </span>
            );
          })}
        {marks.figures
          .filter((figure) => figure.curve_count > 0 && figure.bbox.length === 4)
          .map((figure) => {
            const [x0 = 0, y0 = 0, x1 = 0, y1 = 0] = figure.bbox;
            return (
              <div
                key={figure.figure_uid}
                className="pdf-mark pdf-mark--figure"
                data-mark="figure"
                style={boxStyle({ x0, y0, x1, y1 }, scale)}
              >
                <span className="pdf-mark__tag">
                  <ChartLineIcon aria-hidden />
                  {figureTag(figure)}
                </span>
              </div>
            );
          })}
      </div>
    </>
  );
}
